import { useEffect, useState } from "react";
import type { BlitzlesenExerciseConfig } from "@calega/content";
import type { Account } from "../lib/types";
import { api } from "../lib/api";
import { renderExercise } from "../exercises/registry";
import { ExerciseActivityBoundary } from "../exercises/ExerciseActivityBoundary";
import { useRealtime } from "../realtime/RealtimeContext";

interface IndividualAssignmentViewData {
  id: string;
  title: string;
  exerciseType: "blitzlesen" | "laute-erkennen";
  config: BlitzlesenExerciseConfig | import("@calega/content").LauteExerciseConfig;
  contentPageId: string;
  version: number;
  currentVersion: number;
  updateAvailable: boolean;
  revision: number;
  status: "ACTIVE" | "COMPLETED";
  archivedAt: string | null;
  canManage: boolean;
  learner: { id: string; displayName: string };
}

export function IndividualAssignmentView({ account, assignmentId }: { account: Account; assignmentId: string }) {
  const realtime = useRealtime();
  const [assignment, setAssignment] = useState<IndividualAssignmentViewData | null>(null);
  const [message, setMessage] = useState("");

  async function load() {
    setAssignment(await api<IndividualAssignmentViewData>(`/learning/individual-assignments/${encodeURIComponent(assignmentId)}`));
  }

  useEffect(() => {
    setAssignment(null);
    setMessage("");
    load().catch((error) => setMessage(error instanceof Error ? error.message : "Die individuelle Übung konnte nicht geladen werden."));
  }, [assignmentId, account.actingAs?.id]);

  useEffect(() => {
    if (assignment && realtime.session && account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST")) {
      realtime.publishRoute(`individual-assignment:${assignment.id}`);
    }
  }, [assignment?.id, realtime.session?.id]);

  async function updateVersion() {
    await api(`/learning/individual-assignments/${encodeURIComponent(assignmentId)}/current-version`, { method: "POST" });
    await load();
    setMessage("Die Zuweisung verwendet jetzt die aktuelle Version.");
  }

  async function setStatus(status: "ACTIVE" | "COMPLETED") {
    await api(`/learning/individual-assignments/${encodeURIComponent(assignmentId)}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
    await load();
    setMessage(status === "ACTIVE" ? "Die Zuweisung wurde reaktiviert." : "Die Zuweisung wurde beendet.");
  }

  if (!assignment) return <div className={message ? "alert error" : "loading-card"}>{message || "Individuelle Übung wird geladen …"}</div>;

  return <article className="content-card individual-assignment-page">
    <header className="content-heading">
      <img className="content-mascot" src="/brand/lepi-liest.webp" alt="" />
      <span className="page-kind exercise">Individuelle Übung</span>
      <h1>{assignment.title}</h1>
      <p className="lead">{assignment.exerciseType === "laute-erkennen" ? "Laute erkennen · begleitet" : "Blitzlesen"} für {assignment.learner.displayName} · Inhaltsversion {assignment.version}</p>
      {message && <div className="alert info">{message}</div>}
      {assignment.canManage && <div className="button-row">
        {assignment.updateAvailable && <button className="secondary small" onClick={() => updateVersion().catch((error) => setMessage(error.message))}>Aktuelle Version übernehmen</button>}
        {assignment.status === "ACTIVE"
          ? <button className="secondary small" onClick={() => setStatus("COMPLETED").catch((error) => setMessage(error.message))}>Zuweisung beenden</button>
          : <button className="secondary small" onClick={() => setStatus("ACTIVE").catch((error) => setMessage(error.message))}>Reaktivieren</button>}
      </div>}
      {assignment.updateAvailable && !assignment.canManage && <p className="muted">Eine neue Version ist verfügbar. Deine aktuelle Zuweisung bleibt unverändert.</p>}
      {assignment.archivedAt && <p className="muted">Das Original wurde archiviert. Diese Zuweisung bleibt weiterhin nutzbar.</p>}
      {assignment.status !== "ACTIVE" && <div className="alert info">Diese Zuweisung wurde beendet. Nur eine berechtigte Fachkraft kann sie reaktivieren.</div>}
    </header>
    {assignment.status === "ACTIVE" && <ExerciseActivityBoundary key={assignment.id} pageId={assignment.contentPageId}>{renderExercise(assignment.exerciseType, {
      account,
      pageId: assignment.contentPageId,
      version: assignment.version,
      config: assignment.config,
      individualAssignmentId: assignment.id,
    })}</ExerciseActivityBoundary>}
  </article>;
}
